import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const benefits = [
  {
    icon: 'ri-award-fill',
    title: 'Custom-Engineered — Never Off-the-Shelf',
    desc: 'Every sign is designed and built specifically for your brand, location, and visibility goals. No templates. No shortcuts.',
  },
  {
    icon: 'ri-map-pin-2-fill',
    title: 'Deep Austin Permitting Expertise',
    desc: 'We know every city zone, HOA rule, and permit requirement. That means faster approvals and zero compliance headaches for you.',
  },
  {
    icon: 'ri-loop-right-line',
    title: 'Single Point of Contact, Full Execution',
    desc: 'Design, fabrication, permitting, and installation — one team, one timeline, zero handoffs. Fewer delays, better results.',
  },
  {
    icon: 'ri-shield-check-fill',
    title: 'Built for Long-Term Durability',
    desc: 'Premium-grade materials and professional installation mean your signage investment performs for years without costly repairs.',
  },
  {
    icon: 'ri-flashlight-fill',
    title: 'Fast Turnaround, Precision Execution',
    desc: 'Our streamlined in-house production process means you get production-quality signage on time, every time.',
  },
];

export default function WhyChooseUs() {
  const { ref, visible } = useScrollAnimation();

  return (
    <section className="py-28 bg-[#F4EFE5] overflow-hidden">
      <div className="max-w-7xl mx-auto px-8">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center reveal ${visible ? 'visible' : ''}`}>

          {/* Left: stacked image with floating badge */}
          <div className="relative">
            {/* Main image */}
            <div className="relative overflow-hidden rounded-sm w-full h-[560px]">
              <img
                src="https://public.readdy.ai/ai/img_res/00cd387e2c0bfc8c8d3a3a7df1e5447f.jpg"
                alt="Capital Area Signs professional installation team Austin TX"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>

            {/* Floating inset image */}
            <div className="absolute -bottom-8 -right-8 w-48 h-48 overflow-hidden border-4 border-[#F4EFE5] shadow-2xl hidden md:block">
              <img
                src="https://capitalareasigns.com/wp-content/uploads/2026/01/Realistic_Homepage_3.png"
                alt="Capital Area Signs completed project"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Floating stat badge */}
            <div className="absolute top-6 -left-6 bg-[#C4962A] px-6 py-5 hidden md:block">
              <p className="text-3xl font-bold text-black leading-none"
                style={{ fontFamily: 'Playfair Display, serif' }}>500+</p>
              <p className="text-black/70 text-xs font-bold uppercase tracking-widest mt-1">Signs Installed</p>
            </div>
          </div>

          {/* Right: content */}
          <div className="lg:pl-8">
            <p className="eyebrow mb-5">Why Capital Area Signs</p>
            <h2 className="text-5xl md:text-6xl font-bold text-[#111111] leading-tight mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Businesses<br />
              <em className="not-italic text-gradient-gold">Choose Us</em>
            </h2>
            <span className="gold-bar mb-8 block" />

            <div className="space-y-7 mb-10">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-5 group">
                  <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 bg-[#111111] group-hover:bg-[#C4962A] transition-colors duration-300">
                    <i className={`${b.icon} text-[#C4962A] group-hover:text-black text-sm transition-colors duration-300`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#111111] text-sm mb-1.5 leading-snug"
                      style={{ fontFamily: 'Playfair Display, serif' }}>
                      {b.title}
                    </h3>
                    <p className="text-[#666666] text-sm leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <a href="#quote" className="btn-gold">
                Get a Free Quote
                <i className="ri-arrow-right-line" />
              </a>
              <a href="tel:+15127990115" className="btn-outline-gold">
                <i className="ri-phone-fill" />
                (512) 799-0115
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
